import { useState, useCallback } from 'react';
import * as appointmentService from '../services/appointmentService';
import { config } from '../config/environment';
import { IAppointmentFiltersDTO } from '../types/interfaces/IAppointment';

// Hook unificado para turnos y conteo de turnos pendientes
export function useAppointments() {
  const [remainingCount, setRemainingCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);

  // ===== FUNCIONES BÁSICAS DE TURNOS =====
  const getAllAppointments = useCallback(
    async (filters: IAppointmentFiltersDTO) => {
      return await appointmentService.getAppointments(filters);
    },
    []
  );

  const getUpcomingAppointments = useCallback(
    async (days: number = 7, filters: IAppointmentFiltersDTO) => {
      return await appointmentService.getUpcomingAppointments(days, filters);
    },
    []
  );

  const getAppointmentById = useCallback(async (id: string) => {
    return await appointmentService.getAppointmentById(id);
  }, []);

  const createAppointment = useCallback(async (appointmentData: any) => {
    return await appointmentService.createAppointment(appointmentData);
  }, []);

  const updateAppointment = useCallback(
    async (id: string, appointmentData: any) => {
      return await appointmentService.updateAppointment(id, appointmentData);
    },
    []
  );

  const deleteAppointment = useCallback(async (id: string) => {
    return await appointmentService.deleteAppointment(id);
  }, []);

  // ===== CONTEO DE TURNOS PENDIENTES =====
  const loadRemainingAppointments = useCallback(
    async (filters: IAppointmentFiltersDTO) => {
      try {
        setLoading(true);
        const response = await getAllAppointments(filters);

        if (response.success) {
          const now = new Date();
          const today = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate()
          );
          const endOfDay = new Date(today);
          endOfDay.setHours(23, 59, 59, 999);

          // Filtrar turnos de hoy que están entre ahora y el final del día
          const remainingAppointments = response.data.filter(
            (appointment: any) => {
              const appointmentDate = new Date(appointment.start);
              const isToday =
                appointmentDate.getDate() === today.getDate() &&
                appointmentDate.getMonth() === today.getMonth() &&
                appointmentDate.getFullYear() === today.getFullYear();

              return (
                isToday &&
                appointment.start >= now &&
                appointment.start <= endOfDay
              );
            }
          );

          setRemainingCount(remainingAppointments.length);
        } else {
          setRemainingCount(0);
        }
      } catch (error) {
        setRemainingCount(0);
      } finally {
        setLoading(false);
      }
    },
    [getAllAppointments]
  );

  // Función para inicializar conteo de turnos pendientes
  const initializeRemainingCount = useCallback(
    async (filters: IAppointmentFiltersDTO) => {
      await loadRemainingAppointments(filters);

      // Actualizar según configuración
      const interval = setInterval(
        () => loadRemainingAppointments(filters),
        config.updates.remainingAppointmentsInterval
      );

      return () => clearInterval(interval);
    },
    [loadRemainingAppointments]
  );

  return {
    // Funciones básicas de turnos
    getAllAppointments,
    getUpcomingAppointments,
    getAppointmentById,
    createAppointment,
    updateAppointment,
    deleteAppointment,

    // Estado y funciones de turnos pendientes
    remainingCount,
    loading,
    loadRemainingAppointments,
    initializeRemainingCount,
  };
}
